/**
 * Stage grouping for tournament picks (specs/champions-league, AC-2.2).
 *
 * Deliberately carries NO stage vocabulary. `BACKEND_TO_FRONTEND_STAGE` and
 * `TournamentStageKey` in the World Cup helpers encode a fixed three-round group stage,
 * which is exactly why an eight-matchday league phase does not fit them. Everything here
 * comes off the server's own `stage` / `stageLabel` / `round` / `displayOrder`, so a new
 * tournament needs no client change.
 *
 * Pure — no React, no API types beyond the structural shape.
 */

import type { Matchweek } from './matchweekSections.helper';

export interface StageSectionMatchLike {
  matchId: string;
  stage: string;
  stageLabel: string;
  round: string | null;
  status: 'upcoming' | 'live' | 'finished';
  displayOrder: number;
}

/**
 * A stage section is shape-compatible with `Matchweek`, so it renders through the same
 * view. `round` carries the composite section key rather than a matchweek number.
 */
export interface StageSection<T extends StageSectionMatchLike = StageSectionMatchLike>
  extends Matchweek<T> {
  /** Compact rail label, e.g. "MD 3", "R16", "Final". */
  shortLabel: string;
}

/** Stable key for one section. Stage alone is not enough: a league phase has 8 rounds. */
export function stageSectionKey(stage: string, round: string | null): string {
  return round ? `${stage}#${round}` : stage;
}

/**
 * Compact form for the chip rail, derived from the server's label so a new competition
 * needs no mapping table. Anything unrecognised falls back to the label itself, which is
 * always correct if occasionally long.
 */
export function shortStageLabel(label: string): string {
  const trimmed = label.trim();
  const matchday = /^match(?:day|week)\s+(\d+)$/i.exec(trimmed);
  if (matchday) return `MD ${matchday[1]}`;
  const roundOf = /^round of (\d+)$/i.exec(trimmed);
  if (roundOf) return `R${roundOf[1]}`;
  if (/^quarter[-\s]?finals?$/i.test(trimmed)) return 'QF';
  if (/^semi[-\s]?finals?$/i.test(trimmed)) return 'SF';
  if (/^third[-\s]?place/i.test(trimmed)) return '3rd';
  if (/^play-?off$/i.test(trimmed)) return 'PO';
  return trimmed;
}

/**
 * Group matches into stage sections, ordered chronologically.
 *
 * Ordering is by the section's lowest `displayOrder`, which the linker assigns as the index
 * over date-sorted fixtures. That is a faithful chronological key; stage order is not, and
 * round number is not either once a competition reschedules.
 */
export function buildStageSections<T extends StageSectionMatchLike>(
  matches: readonly T[],
): StageSection<T>[] {
  const byKey = new Map<string, { label: string; matches: T[] }>();

  for (const match of matches) {
    if (!match?.stage) continue;
    const key = stageSectionKey(match.stage, match.round);
    const bucket = byKey.get(key);
    if (bucket) bucket.matches.push(match);
    else byKey.set(key, { label: match.stageLabel || match.stage, matches: [match] });
  }

  return [...byKey.entries()]
    .map(([key, { label, matches: sectionMatches }]) => ({
      round: key,
      label,
      shortLabel: shortStageLabel(label),
      matches: [...sectionMatches].sort((a, b) => a.displayOrder - b.displayOrder),
    }))
    .sort((a, b) => a.matches[0].displayOrder - b.matches[0].displayOrder);
}
